<template>
  <section class="relative overflow-hidden bg-white pb-10 pt-[104px] tablet:bg-bg tablet:pb-16 tablet:pt-[140px]">
    <!-- Blobs -->
    <div
      class="pointer-events-none absolute end-[-120px] top-[55px] z-0 size-[473px] rounded-full bg-gold-primary-20 opacity-80 blur-[80px] tablet:end-[-200px] tablet:top-0 tablet:size-[778px]"
    ></div>
    <div
      class="pointer-events-none absolute start-[-80px] top-[440px] z-0 size-[267px] rounded-full bg-gold-primary-20 opacity-80 blur-[80px] tablet:start-[-200px] tablet:top-[300px] tablet:size-[427px]"
    ></div>

    <div
      class="container relative z-10 grid items-center gap-10 tablet:grid-cols-[1.1fr_1fr] tablet:gap-12 [&>*]:min-w-0"
    >
      <!-- Copy -->
      <div class="flex flex-col items-center gap-5 text-center tablet:items-start tablet:text-start">
        <p
          v-if="isUae"
          class="text-xs font-semibold uppercase tracking-[0.15em] text-gold-primary-120"
        >
          {{ t('landing.uae.hero.eyebrow') }}
        </p>
        <i18n-t
          :keypath="isUae ? 'landing.uae.hero.title' : 'landing.hero.title'"
          scope="global"
          tag="h1"
          class="text-balance text-[28px] font-extrabold leading-[1.1] tracking-[-0.03em] tablet:text-[56px] tablet:leading-[1.05] desktop:text-[64px]"
        >
          <template #accent
            ><span class="text-gold-primary-120">{{
              t(isUae ? 'landing.uae.hero.titleAccent' : 'landing.hero.titleAccent')
            }}</span></template
          >
        </i18n-t>
        <p class="max-w-[min(44ch,100%)] text-balance text-base text-black-80 tablet:text-lg">
          {{ subtitle }}
        </p>
        <NuxtLink
          :to="applyHref"
          @click="trackCtaClick('hero')"
          class="inline-flex h-14 w-full max-w-[293px] items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-gold-primary-100 to-gold-primary-120 px-7 text-base font-bold text-black-100 shadow-[0_2px_0_rgba(0,0,0,0.04),0_8px_20px_rgba(237,164,54,0.25)] transition-transform hover:-translate-y-px"
        >
          {{ t(isUae ? 'landing.uae.hero.cta' : 'landing.hero.cta') }}
          <span
            aria-hidden="true"
            class="rtl:inline-block rtl:-scale-x-100"
            >→</span
          >
        </NuxtLink>
        <p
          v-if="isUae"
          class="text-[13px] font-medium text-black-60"
        >
          {{ t('landing.uae.hero.trust') }}
        </p>
      </div>

      <!-- Search Mock — now driven by real catalogue data with signup-gate funnel -->
      <div
        ref="cardRef"
        class="min-w-0 max-w-full overflow-hidden rounded-2xl bg-white p-5 shadow-xl shadow-black/5 ring-1 ring-black/[0.06] transition-[transform,opacity] duration-500 tablet:p-6"
        :class="isRevealed ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'"
      >
        <!-- Search bar -->
        <div
          class="relative flex items-center gap-2 rounded-xl border border-black/[0.08] bg-bg px-3 py-2.5 text-sm text-black-100"
          :class="{ 'ring-2 ring-gold-primary-100/40': isInputFocused }"
        >
          <svg
            viewBox="0 0 24 24"
            class="size-4 shrink-0 text-black-60"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            v-model="query"
            type="text"
            :placeholder="placeholderText"
            class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-black-100 placeholder:text-black-60 focus:outline-none focus:ring-0"
            :aria-label="t('landing.hero.searchAriaLabel')"
            @focus="isInputFocused = true"
            @blur="isInputFocused = false"
          />
          <NuxtLink
            :to="tryItHref"
            @click="trackCtaClick('search')"
            class="ms-auto inline-flex shrink-0 items-center gap-1 rounded-md bg-gold-primary-10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-primary-120 transition-colors hover:bg-gold-primary-20"
          >
            {{ t('landing.hero.tryIt') }}
            <span
              aria-hidden="true"
              class="rtl:inline-block rtl:-scale-x-100"
              >→</span
            >
          </NuxtLink>
        </div>

        <!-- Filter chip row -->
        <div
          ref="chipRowRef"
          class="relative mt-3 flex flex-wrap gap-2"
        >
          <div
            v-for="chip in filterChips"
            :key="chip.key"
            class="relative"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors"
              :class="
                isChipActive(chip.key)
                  ? 'border-gold-primary-100 bg-gold-primary-10 text-gold-primary-120'
                  : 'border-black/[0.08] bg-white text-black-80 hover:bg-black/[0.03]'
              "
              @click="onChipClick(chip.key)"
            >
              {{ chipLabel(chip) }}
              <span
                v-if="isChipActive(chip.key)"
                class="ms-0.5 cursor-pointer text-gold-primary-120 hover:text-gold-primary-140"
                role="button"
                :aria-label="t('landing.hero.clearFilter')"
                @click.stop="clearFilter"
              >
                ✕
              </span>
              <span
                v-else
                class="text-black-60"
              >
                ▾
              </span>
            </button>

            <!-- Searchable popover (developer / area) — full lists -->
            <div
              v-if="openChip === chip.key && (chip.key === 'developer' || chip.key === 'area')"
              class="absolute start-0 top-full z-20 mt-1.5 w-[230px] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/[0.08]"
            >
              <div class="border-b border-black/[0.06] p-2">
                <input
                  v-if="chip.key === 'developer'"
                  v-model="developerQuery"
                  type="text"
                  :placeholder="t('landing.hero.searchDevelopers')"
                  class="w-full rounded-md bg-bg px-2.5 py-1.5 text-xs text-black-100 placeholder:text-black-50 focus:outline-none"
                  @click.stop
                />
                <input
                  v-else
                  v-model="areaQuery"
                  type="text"
                  :placeholder="t('landing.hero.searchAreas')"
                  class="w-full rounded-md bg-bg px-2.5 py-1.5 text-xs text-black-100 placeholder:text-black-50 focus:outline-none"
                  @click.stop
                />
              </div>
              <div class="max-h-[220px] overflow-y-auto py-1">
                <button
                  v-for="opt in chip.key === 'developer' ? filteredDeveloperOptions : filteredAreaOptions"
                  :key="opt.value"
                  type="button"
                  class="block w-full truncate px-3 py-2 text-start text-xs text-black-80 transition-colors hover:bg-bg hover:text-black-100"
                  @click="applyFilter(chip.key, opt.value)"
                >
                  {{ opt.label }}
                </button>
                <p
                  v-if="!(chip.key === 'developer' ? filteredDeveloperOptions : filteredAreaOptions).length"
                  class="px-3 py-3 text-center text-xs text-black-50"
                >
                  {{ t('landing.hero.noMatches') }}
                </p>
              </div>
            </div>

            <!-- Popover (price / stage) -->
            <div
              v-else-if="openChip === chip.key && chip.options.length"
              class="absolute start-0 top-full z-20 mt-1.5 w-[180px] overflow-hidden rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/[0.08]"
            >
              <button
                v-for="opt in chip.options"
                :key="opt.value"
                type="button"
                class="block w-full truncate px-3 py-2 text-start text-xs text-black-80 transition-colors hover:bg-bg hover:text-black-100"
                @click="applyFilter(chip.key, opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>

            <!-- Gated popover (chip with no client-side data, e.g. Beds) -->
            <div
              v-else-if="openChip === chip.key && !chip.options.length"
              class="absolute start-0 top-full z-20 mt-1.5 w-[200px] overflow-hidden rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/[0.08]"
            >
              <p class="text-xs text-black-80">{{ t('landing.hero.filtersAfterSignup') }}</p>
              <NuxtLink
                :to="tryItHref"
                @click="trackCtaClick('search')"
                class="mt-2 inline-flex items-center gap-1 rounded-md bg-gold-primary-10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-primary-120 transition-colors hover:bg-gold-primary-20"
              >
                {{ t('landing.hero.signUp') }}
                <span
                  aria-hidden="true"
                  class="rtl:inline-block rtl:-scale-x-100"
                  >→</span
                >
              </NuxtLink>
            </div>
          </div>
        </div>

        <!-- Result rows -->
        <div class="mt-4 flex max-h-[360px] flex-col gap-2 overflow-y-auto pe-0.5">
          <template v-if="resultState === 'loading'">
            <div
              v-for="n in 4"
              :key="`skel-${n}`"
              class="flex items-center justify-between rounded-xl border border-black/[0.05] bg-white px-3 py-3"
            >
              <div class="min-w-0 flex-1">
                <div class="h-3 w-2/3 animate-pulse rounded bg-black/[0.08]"></div>
                <div class="mt-2 h-2.5 w-1/2 animate-pulse rounded bg-black/[0.06]"></div>
              </div>
              <div class="ms-3 h-6 w-16 shrink-0 animate-pulse rounded bg-black/[0.06]"></div>
            </div>
          </template>

          <template v-else-if="resultState === 'rows'">
            <NuxtLink
              v-for="row in displayedProjects"
              :key="row.key"
              :to="row.href"
              no-prefetch
              class="flex items-center justify-between rounded-xl border border-black/[0.05] bg-white px-3 py-3 transition-colors hover:bg-bg"
            >
              <div class="min-w-0">
                <div class="truncate text-sm font-bold text-black-100">{{ row.name }}</div>
                <div class="truncate text-xs text-black-60">{{ row.subtitle }}</div>
                <div
                  v-if="row.detail"
                  class="mt-0.5 truncate text-[11px] font-medium text-black-80"
                >
                  {{ row.detail }}
                </div>
              </div>
              <div class="ms-3 shrink-0 text-end">
                <div class="text-[10px] uppercase tracking-wider text-black-60">{{ t('landing.hero.priceFrom') }}</div>
                <div class="text-sm font-bold text-gold-primary-120">{{ row.priceLabel }}</div>
              </div>
            </NuxtLink>
          </template>

          <!-- Only a visitor's own search/filter can reach "No matches". -->
          <template v-else-if="resultState === 'no-matches'">
            <div
              class="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-black/[0.08] bg-bg px-3 py-8 text-center"
            >
              <p class="text-sm font-semibold text-black-100">{{ t('landing.hero.noMatches') }}</p>
              <p class="text-xs text-black-60">
                {{ noMatchesHint }}
              </p>
            </div>
          </template>

          <template v-else>
            <p
              class="rounded-xl border border-dashed border-black/[0.08] bg-bg px-3 py-8 text-center text-xs text-black-60"
            >
              {{ t('landing.hero.featuredUnavailable') }}
            </p>
          </template>
        </div>

        <NuxtLink
          :to="tryItHref"
          @click="trackCtaClick('search')"
          class="mt-3 flex items-center justify-center gap-1 rounded-xl bg-gold-primary-10 px-3 py-2.5 text-xs font-bold text-gold-primary-120 transition-colors hover:bg-gold-primary-20"
        >
          {{ t('landing.hero.signUpSeeAll') }}
          <span
            aria-hidden="true"
            class="rtl:inline-block rtl:-scale-x-100"
            >→</span
          >
        </NuxtLink>

        <!-- Match count -->
        <div class="mt-4 flex items-center justify-between border-t border-black/[0.06] pt-3 text-xs text-black-60">
          <i18n-t
            v-if="showMatchCount"
            keypath="landing.hero.matches"
            scope="global"
            tag="span"
            :plural="serverTotal"
          >
            <template #count
              ><b class="font-bold text-gold-primary-120">{{ formatNumber(serverTotal) }}</b></template
            >
          </i18n-t>
          <span v-else-if="counterText">{{ counterText }}</span>
          <span v-else></span>
          <span v-if="isSearching">{{ t('landing.hero.sortedByRelevance') }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onClickOutside, useIntersectionObserver, useDebounceFn } from '@vueuse/core';
import { PROPERTIES_DICTIONARY_KEYS, usePCDictionary } from '~/api/queries/dictionaries';
import { useLandingFeaturedProjects } from '~/composables/useLandingFeaturedProjects';
import {
  catalogueTotalLabel,
  formatAedShort,
  heroResultState,
  toHeroProjectRow,
  type HeroSourceProject,
} from '~/utils/landing/heroProjects';
import type { SiteMarket } from '~/constants/market';
import type { HeroProjectRow, LandingProjectStage } from '~/types/landing';

type FilterKey = 'developer' | 'area' | 'price' | 'beds' | 'stage';
type FilterOption = { value: string; label: string };
type FilterChip = { key: FilterKey; label: string; options: FilterOption[] };

const { t } = useI18n();
const { formatNumber } = useFormat();

// `labelKey` is translated at render; `value` is the stable filter id.
const PRICE_RANGES: { value: string; labelKey: string; min: number; max: number | null }[] = [
  { value: 'under-1m', labelKey: 'landing.hero.priceRanges.under1m', min: 0, max: 1_000_000 },
  { value: '1-3m', labelKey: 'landing.hero.priceRanges.from1to3m', min: 1_000_000, max: 3_000_000 },
  { value: '3-10m', labelKey: 'landing.hero.priceRanges.from3to10m', min: 3_000_000, max: 10_000_000 },
  { value: '10m-plus', labelKey: 'landing.hero.priceRanges.over10m', min: 10_000_000, max: null },
];

const STAGE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'pre-launch', labelKey: 'landing.hero.stages.preLaunch' },
  { value: 'eoi', labelKey: 'landing.hero.stages.eoi' },
  { value: 'on-sale', labelKey: 'landing.hero.stages.onSale' },
  { value: 'sold-out', labelKey: 'landing.hero.stages.soldOut' },
];

const STAGE_LABEL_KEYS: Record<LandingProjectStage, string> = {
  'pre-launch': 'landing.hero.stages.preLaunch',
  eoi: 'landing.hero.stages.eoi',
  'on-sale': 'landing.hero.stages.onSale',
  'sold-out': 'landing.hero.stages.soldOut',
  ready: 'landing.hero.stages.ready',
};

// The typed search renders at most this many rows.
const SEARCH_RESULT_LIMIT = 30;

const { featuredRows, featuredTotal, featuredPending } = useLandingFeaturedProjects();
const route = useRoute();

// Full developer + area lists (public catalog dictionaries) so the chip
// dropdowns aren't limited to whatever happens to be in the cached top set.
const { data: developersDict } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.DEVELOPER_NAMES);
const { data: locationsDict } = usePCDictionary(PROPERTIES_DICTIONARY_KEYS.LOCATIONS);

const query = ref('');
const isInputFocused = ref(false);
const activeFilter = ref<{ key: FilterKey | null; value: string | null }>({ key: null, value: null });
const openChip = ref<FilterKey | null>(null);

const cardRef = ref<HTMLElement | null>(null);
const chipRowRef = ref<HTMLElement | null>(null);
const isRevealed = ref(false);

const placeholderText = computed(() => {
  if (isInputFocused.value || query.value) return t('landing.hero.placeholderFocused');
  return t('landing.hero.placeholderIdle');
});

const { market: marketProp } = defineProps<{ market?: SiteMarket }>();
const { track } = useLandingPage();
const isUae = computed(() => (marketProp ?? track.value) === 'uae');
const { copyParams: uaeCopy } = useUaeModel();

const { topSelfClosePercent, totalProjects } = useLandingFacts();
const subtitle = computed(() => {
  if (isUae.value) return t('landing.uae.hero.subtitle', uaeCopy.value);
  return topSelfClosePercent.value === null
    ? t('landing.hero.subtitleFallback')
    : t('landing.hero.subtitle', { percent: topSelfClosePercent.value });
});
const noMatchesHint = computed(() =>
  totalProjects.value === null
    ? t('landing.hero.noMatchesHintFallback')
    : t('landing.hero.noMatchesHint', { count: formatNumber(totalProjects.value) }),
);

const developerOptions = computed<FilterOption[]>(() =>
  ((developersDict.value as Array<{ id: number; name: string }> | undefined) ?? [])
    .map((d) => ({ value: d.name, label: d.name }))
    .filter((o) => o.label?.trim())
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const areaOptions = computed<FilterOption[]>(() =>
  ((locationsDict.value as Array<{ id: number; name: string }> | undefined) ?? [])
    .map((l) => ({ value: l.name, label: l.name }))
    .filter((o) => o.label?.trim())
    .sort((a, b) => a.label.localeCompare(b.label)),
);

// In-dropdown search over the (now full) developer + area lists.
const developerQuery = ref('');
const areaQuery = ref('');
const filteredDeveloperOptions = computed(() => {
  const q = developerQuery.value.trim().toLowerCase();
  return q ? developerOptions.value.filter((o) => o.label.toLowerCase().includes(q)) : developerOptions.value;
});
const filteredAreaOptions = computed(() => {
  const q = areaQuery.value.trim().toLowerCase();
  return q ? areaOptions.value.filter((o) => o.label.toLowerCase().includes(q)) : areaOptions.value;
});
// Reset the in-dropdown search when its chip closes.
watch(openChip, (next, prev) => {
  if (prev === 'developer' && next !== 'developer') developerQuery.value = '';
  if (prev === 'area' && next !== 'area') areaQuery.value = '';
});

const priceOptions = computed<FilterOption[]>(() =>
  PRICE_RANGES.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
);

// Beds requires apartments_summary or unit detail — not in the anon list payload, so this stays gated.
const bedsOptions = computed<FilterOption[]>(() => []);

// Static stages; the server's sale_status filter does the matching.
const stageOptions = computed<FilterOption[]>(() =>
  STAGE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
);

const filterChips = computed<FilterChip[]>(() => [
  { key: 'developer', label: t('landing.hero.chips.developer'), options: developerOptions.value },
  { key: 'area', label: t('landing.hero.chips.area'), options: areaOptions.value },
  { key: 'price', label: t('landing.hero.chips.price'), options: priceOptions.value },
  { key: 'beds', label: t('landing.hero.chips.beds'), options: bedsOptions.value },
  { key: 'stage', label: t('landing.hero.chips.stage'), options: stageOptions.value },
]);

// ── Server-backed search (full catalog, ranked) ────────────────────────────
// The default (no query/filter) shows the SSR'd featured rows. As soon as the
// visitor types or picks a chip, we query /api/properties/v2/list (non-exclusive
// only, priced only) so results span the public catalogue.
const STAGE_CODE_MAP: Record<string, string> = {
  'pre-launch': 'PRELAUNCH',
  eoi: 'EOI',
  'on-sale': 'ON_SALE',
  'sold-out': 'SOLD_OUT',
};

const serverItems = ref<HeroSourceProject[]>([]);
const serverTotal = ref(0);
const searchPending = ref(false);

const isSearching = computed(
  () => query.value.trim().length >= 2 || Boolean(activeFilter.value.key && activeFilter.value.value),
);

const buildListParams = (): Record<string, string | number> => {
  // Public widget: never exclusives; priced rows only so the count matches what we render.
  const params: Record<string, string | number> = {
    page: 1,
    limit: SEARCH_RESULT_LIMIT,
    is_exclusive: 'false',
    min_price: 1,
  };
  const q = query.value.trim();
  if (q) params.search = q;
  const { key, value } = activeFilter.value;
  if (key && value) {
    if (key === 'developer') params.developer_names = value;
    else if (key === 'area') params.area = value;
    else if (key === 'price') {
      const r = PRICE_RANGES.find((x) => x.value === value);
      if (r) {
        params.min_price = Math.max(r.min, 1);
        if (r.max != null) params.max_price = r.max;
      }
    } else if (key === 'stage') {
      const code = STAGE_CODE_MAP[value];
      if (code) params.sale_status = code;
    }
  }
  if (!q) {
    params.order_by = 'updated_at';
    params.order_way = 'desc';
  }
  return params;
};

const fetchServer = async () => {
  if (!isSearching.value) {
    serverItems.value = [];
    serverTotal.value = 0;
    return;
  }
  searchPending.value = true;
  try {
    const res = await $fetch<{ data?: { items?: HeroSourceProject[]; total?: number } }>('/api/properties/v2/list', {
      params: buildListParams(),
    });
    serverItems.value = Array.isArray(res?.data?.items) ? res.data.items : [];
    serverTotal.value = Number(res?.data?.total ?? serverItems.value.length);
  } catch {
    serverItems.value = [];
    serverTotal.value = 0;
  } finally {
    searchPending.value = false;
  }
};

const debouncedFetchServer = useDebounceFn(fetchServer, 250);
watch([query, activeFilter], () => debouncedFetchServer(), { deep: true });

type DisplayRow = {
  key: string;
  name: string;
  subtitle: string;
  detail: string;
  priceLabel: string;
  href: string;
};

// toHeroProjectRow drops exclusive / resale / unpriced items client-side as a second guard.
const searchRows = computed<HeroProjectRow[]>(() =>
  serverItems.value
    .map((p) => toHeroProjectRow(p))
    .filter((row): row is HeroProjectRow => row !== null)
    .slice(0, SEARCH_RESULT_LIMIT),
);

const rowDetail = (row: HeroProjectRow): string => {
  if (row.handover) return t('landing.hero.handover', { date: row.handover });
  return row.stage ? t(STAGE_LABEL_KEYS[row.stage]) : '';
};

const displayedProjects = computed<DisplayRow[]>(() =>
  (isSearching.value ? searchRows.value : featuredRows.value).map((row) => ({
    key: `${row.developerSlug}/${row.projectSlug}/${row.name}`,
    name: row.name || t('landing.hero.projectFallback'),
    subtitle: [row.developer, row.area].filter(Boolean).join(' · '),
    detail: rowDetail(row),
    priceLabel: formatAedShort(row.startingPriceAed),
    href: row.developerSlug && row.projectSlug ? `/projects/${row.developerSlug}/${row.projectSlug}` : applyHref.value,
  })),
);

const resultState = computed(() =>
  heroResultState({
    isSearching: isSearching.value,
    searchPending: searchPending.value,
    searchRowCount: searchRows.value.length,
    featuredPending: featuredPending.value,
    featuredRowCount: featuredRows.value.length,
  }),
);

const catalogueTotal = computed(() => catalogueTotalLabel(totalProjects.value ?? featuredTotal.value, formatNumber));

// Footer counter: real server count while searching, "4 of 1,200+ projects" by default, never "—".
const counterText = computed<string | null>(() => {
  const count = featuredRows.value.length;
  if (isSearching.value || !count) return null;
  return catalogueTotal.value
    ? t('landing.hero.featuredOfTotal', { count, total: catalogueTotal.value })
    : t('landing.hero.projectsCount', count);
});
const showMatchCount = computed(() => isSearching.value && resultState.value !== 'loading');

const isChipActive = (key: FilterKey) => activeFilter.value.key === key && !!activeFilter.value.value;

const chipLabel = (chip: FilterChip): string => {
  if (!isChipActive(chip.key)) return chip.label;
  const opt = chip.options.find((o) => o.value === activeFilter.value.value);
  return opt?.label || chip.label;
};

const onChipClick = (key: FilterKey) => {
  if (openChip.value === key) {
    openChip.value = null;
    return;
  }
  openChip.value = key;
};

const applyFilter = (key: FilterKey, value: string) => {
  activeFilter.value = { key, value };
  openChip.value = null;
};

const clearFilter = () => {
  activeFilter.value = { key: null, value: null };
  openChip.value = null;
};

onClickOutside(chipRowRef, () => {
  if (openChip.value) openChip.value = null;
});

const { signupHref: applyHref } = useSignupHref({ redirect: () => route.fullPath });
const { trackCtaClick } = useLandingTracking();

const tryItHref = applyHref;

useIntersectionObserver(
  cardRef,
  ([entry]) => {
    if (entry.isIntersecting) isRevealed.value = true;
  },
  { threshold: 0.25 },
);
</script>

<style scoped lang="scss"></style>
